import { useCapabilities, shellBlockedKey } from '../../../../services/capabilities';
import React from 'react';
import { setNodeDragImage, GHOST_CENTERED } from '../../utils/dragGhost';
import { useDispatch, useSelector } from 'react-redux';
import { setSelectedNodeClass } from '../../../../store/workflow/workflowSlice';
import { RootState } from '../../../../store';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { typeLabelOf, shapeClassOf, isOnHoldType } from '../../nodeCatalog';

// 표시 이름은 typeLabelOf(type) — 번역 키 한 곳에서 정한다 (예전 예시 인스턴스 목록은 쓰이지 않아 걷었다)
interface NodeType {
  type: string;
  category: string;
  instances: any[];
  icon: React.ReactNode;
}

const nodeTypes: NodeType[] = [
  {
    type: 'csvData',
    category: 'data',
    instances: [],
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
        <polyline points="14 2 14 8 20 8"></polyline>
        <line x1="16" y1="13" x2="8" y2="13"></line>
        <line x1="16" y1="17" x2="8" y2="17"></line>
      </svg>
    ),
  },
  {
    type: 'rdbData',
    category: 'data',
    instances: [],
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <ellipse cx="12" cy="5" rx="9" ry="3"></ellipse>
        <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"></path>
        <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"></path>
      </svg>
    ),
  },
  {
    type: 'transformLoad',
    category: 'process',
    instances: [],
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path d="M4 7h11" />
        <path d="M12 4l3 3-3 3" />
        <path d="M20 17H9" />
        <path d="M12 14l-3 3 3 3" />
      </svg>
    ),
  },
  {
    type: 'filter',
    category: 'process',
    instances: [],
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
      </svg>
    ),
  },
  {
    type: 'join',
    category: 'process',
    instances: [],
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="10"></circle>
        <path d="M8 12h8"></path>
        <path d="M12 8v8"></path>
      </svg>
    ),
  },
  {
    type: 'branch',
    category: 'control',
    instances: [],
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path d="M16 3h5v5" />
        <path d="M4 20L21 3" />
        <path d="M21 16v5h-5" />
        <path d="M15 15l6 6" />
        <path d="M4 4l5 5" />
      </svg>
    ),
  },
  {
    type: 'merge',
    category: 'control',
    instances: [],
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path d="M8 3H3v5" />
        <path d="M3 8l5 5" />
        <path d="M21 3h-5v5" />
        <path d="M16 8l5-5" />
        <path d="M12 20v-8" />
        <path d="M15 12h-6" />
      </svg>
    ),
  },
  {
    type: 'userConfirm',
    category: 'control',
    instances: [],
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="10" />
        <path d="M12 16v-4" />
        <path d="M12 8h.01" />
      </svg>
    ),
  },
  {
    type: 'runWorkflow',
    category: 'exec',
    instances: [],
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
    ),
  },
  {
    type: 'shellCommand',
    category: 'exec',
    instances: [],
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M7 9l3 3-3 3" /><path d="M12 15h5" /></svg>
    ),
  },
  {
    type: 'sqlScript',
    category: 'exec',
    instances: [],
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><ellipse cx="12" cy="5" rx="8" ry="3" /><path d="M4 5v14c0 1.7 3.6 3 8 3s8-1.3 8-3V5" /><path d="M9 11l-2 2 2 2M15 11l2 2-2 2" /></svg>
    ),
  },
  {
    type: 'aggregate',
    category: 'process',
    instances: [],
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M18 4H6l6 8-6 8h12" /></svg>
    ),
  },
  {
    type: 'sort',
    category: 'process',
    instances: [],
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M4 6h16M4 12h10M4 18h5" /><path d="M18 10v8m0 0l-3-3m3 3l3-3" /></svg>
    ),
  },
  {
    type: 'lookup',
    category: 'process',
    instances: [],
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><circle cx="10" cy="10" r="6" /><path d="M20 20l-5.5-5.5" /></svg>
    ),
  },
  {
    // 데이터비교 — 두 데이터를 키로 짝지어 값 수준 비교, 결과는 SAM 레포트 (2026-10-20)
    type: 'compare',
    category: 'process',
    instances: [],
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><rect x="3" y="5" width="7" height="14" rx="1" /><rect x="14" y="5" width="7" height="14" rx="1" /><path d="M10 9h4M10 15h4" strokeDasharray="1.5 1.5" /></svg>
    ),
  },
  {
    type: 'quality',
    category: 'process',
    instances: [],
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z" /><path d="M9 12l2 2 4-4" /></svg>
    ),
  },
  {
    type: 'subflow',
    category: 'group',
    instances: [],
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <rect x="4" y="4" width="16" height="16" rx="4" />
        <circle cx="12" cy="12" r="4" />
      </svg>
    ),
  },
  {
    type: 'repeatGroup',
    category: 'group',
    instances: [],
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <rect x="4" y="4" width="16" height="16" rx="4" />
        <circle cx="12" cy="12" r="4" />
        <path d="M8 20c0-2.21 3.58-4 8-4" stroke="currentColor" />
      </svg>
    ),
  },
  {
    type: 'innerData',
    category: 'data',
    instances: [],
    icon: (
      /* 흐름 중간에 머무는 데이터 — 상자를 지나가는 화살표. 글자(약어) 대신 그림으로 (약어는 쓰지 않는다) */
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <rect x="4" y="6" width="16" height="12" rx="3" />
        <path d="M2 12h4" />
        <path d="M18 12h4" />
        <path d="M10 9l3 3-3 3" />
      </svg>
    ),
  },
  {
    type: 'memo',
    category: 'etc',
    instances: [],
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path d="M4 4h12l4 4v12H4z" />
        <path d="M16 4v4h4" />
        <path d="M8 12h8M8 16h5" />
      </svg>
    ),
  },
];

// 노드 타입별 3자리 약어 매핑
const ClassPanel: React.FC<{ onSelectClass: (type: string) => void; selectedClass: string | null }> = ({
  onSelectClass,
  selectedClass,
}) => {
  const { t } = useTranslation();
  // 외부명령어 — 웹 버전은 기본으로 꺼져 있고, 켜도 권한자만 (서버 services/shell_policy). 막혔으면 흐리게·끌 수 없게
  const shellBlocked = shellBlockedKey(useCapabilities());
  const blocked = (type: string) => type === 'shellCommand' && !!shellBlocked;
  const onDragStart = (event: React.DragEvent<HTMLDivElement>, node: NodeType) => {
    if (blocked(node.type)) { event.preventDefault(); return; }
    const targetRect = event.currentTarget.getBoundingClientRect();
    const offsetX = event.clientX - targetRect.left;
    const offsetY = event.clientY - targetRect.top;
    event.dataTransfer.setData('application/reactflow-node', JSON.stringify({
      type: node.type,
      data: {
        label: typeLabelOf(node.type),
        config: { label: typeLabelOf(node.type) },
      },
      offsetX,
      offsetY,
      source: 'toolbox',
      [GHOST_CENTERED]: true,
    }));
    event.dataTransfer.effectAllowed = 'move';
    // 끄는 동안 툴박스 항목이 아니라 캔버스에 놓일 노드 모양으로 보인다 (utils/dragGhost)
    setNodeDragImage(event, node.type, typeLabelOf(node.type), event.currentTarget);
  };

  const renderNodeTypesByCategory = (category: string) => {
    return (
      <div key={category}>
        <h4 className="text-xs font-medium text-gray-500 mb-1.5">
          {['data', 'process', 'control', 'group', 'etc', 'exec'].includes(category) ? t(`workflow:toolbox.${category}`) : category}
        </h4>
        <div className="space-y-1">
          {nodeTypes
            .filter((node) => node.category === category)
            .map((node) => (
              <div
                key={node.type}
                // 사용 보류 유형은 리본을 붙이고 흐리게 — 쓸 수는 있다(기존 워크플로가 쓰고 있다)
                title={blocked(node.type) ? t(shellBlocked!) : isOnHoldType(node.type) ? t('workflow:toolbox.onHoldTitle') : undefined}
                className={classNames(
                  'relative overflow-hidden flex items-center px-2 py-1 border bg-white cursor-pointer transition-all',
                  isOnHoldType(node.type) && 'of-onhold',
                  blocked(node.type) && 'opacity-40 !cursor-not-allowed',
                  shapeClassOf(node.type),          // 캔버스와 같은 도형 (nodeCatalog 가 정한다)
                  selectedClass === node.type
                    ? 'border-blue-500 shadow-sm'
                    : 'border-gray-200 hover:border-blue-500 hover:shadow-sm'
                )}
                onClick={() => onSelectClass(node.type)}
                data-point={`toolbox:${node.type}`}   // 도움말·AI 가 손가락으로 가리킬 자리 (help/pointer)
                draggable={!blocked(node.type)}
                onDragStart={(e) => onDragStart(e, node)}
              >
                {/* 도형 그림은 w-6 으로 그려져 있다 — 툴박스에서는 한 단계 작게 */}
                <div className="w-6 h-6 flex items-center justify-center text-gray-600 [&_svg]:w-5 [&_svg]:h-5">
                  {node.icon}
                </div>
                <span className="ml-2 text-[0.8125rem] text-gray-700 truncate">{typeLabelOf(node.type)}</span>
                {isOnHoldType(node.type) && <span className="of-onhold-ribbon">{t('workflow:toolbox.onHoldShort')}</span>}
              </div>
            ))}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {TOOLBOX_CATEGORIES.map((c) => renderNodeTypesByCategory(c))}
    </div>
  );
};

const ToolClassPanel: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const selectedClass = useSelector((state: RootState) => state.workflow.selectedNodeClass);

  const handleSelectClass = (type: string) => {
    dispatch(setSelectedNodeClass(type));
  };

  return (
    // flex-col + min-h-0: 제목은 고정하고 목록만 남은 높이 안에서 스크롤한다
    // (이전에는 목록 div 에 높이 제약이 없어 overflow-y-auto 가 동작하지 않고 화면 아래로 넘쳤다)
    <div className="h-full flex flex-col p-3 min-h-0">
      <h3 className="text-base font-medium text-gray-900 mb-2.5 shrink-0">{t('common:ui.toolbox')}</h3>
      <div className="flex-1 min-h-0 overflow-y-auto pr-2">
        <ClassPanel onSelectClass={handleSelectClass} selectedClass={selectedClass} />
      </div>
    </div>
  );
};

/** 툴박스의 묶음 순서 — 다른 화면(속성 일괄수정의 노드 목록 등)도 툴박스와 같은 순서로 보이게 이것을 쓴다 */
export const TOOLBOX_CATEGORIES = ['data', 'process', 'control', 'group', 'exec', 'etc'] as const;

/** 툴박스에 나오는 노드 유형과 묶음(툴박스와 같은 순서) */
export const TOOLBOX_NODE_TYPES: ReadonlyArray<{ type: string; category: string }> =
  TOOLBOX_CATEGORIES.flatMap((c) => nodeTypes.filter((n) => n.category === c).map((n) => ({ type: n.type, category: n.category })));

export default ToolClassPanel; 